/**
 * Pure helper: turn wizard Page-3 input into `shop_bays` insert payloads.
 *
 * Rules locked by the R5 plan + Batch 16-B per-bay-type extension:
 *   - Each technician's `bays_allocated` count produces that many
 *     `shop_bays` rows. Total physical bays = Σ tech.bays_allocated.
 *   - Specialty selections CONVERT one of the primary tech's existing
 *     bays. They do NOT add an extra bay.
 *   - A tech who is primary for K specialties gets K of their bays
 *     converted (one per specialty), each to its specialty `bay_type`.
 *   - 0-bay technicians produce no bays (FOH / managers / advisors).
 *   - Batch 16-B: when the wizard supplies `tech.bay_types[slot]`, the
 *     operator's per-slot choice is used for that slot. Specialty
 *     conversions still take precedence over the dropdown so the
 *     existing "specialty consumes one of your bays" product rule
 *     holds — see the `specForSlot` branch below.
 *   - When `tech.bay_types` is missing or its length is shorter than
 *     `bays_allocated`, the wizard's canonical default
 *     (`DEFAULT_SETUP_BAY_TYPE`, currently `'flat'`) fills any gap.
 *   - `bay_category` is derived from the final `bay_type` (the four
 *     specialty values + `'inspection'` → `'specialty'`; everything
 *     else → `'general'`) so a `wash` bay categorized via the dropdown
 *     surfaces as a specialty row to downstream consumers, the same way
 *     it would if it had been selected via the specialty toggle.
 *
 * Output: bay payloads WITHOUT `assigned_tech_id` populated. The wizard
 * commit step substitutes the actual technician UUID after each tech
 * row is inserted — `__techIndex` carries the link.
 *
 * Layout: columns per tech, bays stacked vertically. Pixel positions
 * chosen to give the existing birdseye editor a clean starting grid
 * (refinable post-wizard).
 */

import type { WizardSpecialty, WizardTech } from './wizardValidation';
// Batch 10: use the canonical Birdseye bay dimensions so the
// onboarding-generated starter layout renders at the same size that
// the rest of the app expects. Each bay's width/height comes from its
// FINAL bay_type via the Facility Editor's own per-type defaults
// (defaultFootprintForBayType), so specialty bays keep their specialty
// envelopes. Grid pitch still uses the standard BAY_WIDTH/BAY_HEIGHT.
import { BAY_WIDTH, BAY_HEIGHT, BAY_SPACING } from '@/components/facility/constants';
import { defaultFootprintForBayType } from '@/types/facility';
import { DEFAULT_SETUP_BAY_TYPE, type BayTypeEnum } from '@/utils/bayTypeLabels';

export interface GeneratedBayPayload {
  /** Stable index back to the wizard's tech rows (resolved to a UUID by commitWizard). */
  __techIndex: number;
  name: string;
  bay_number: number;
  bay_type: BayTypeEnum;
  bay_category: 'general' | 'specialty';
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  is_active: true;
}

export interface GenerateBayLayoutInput {
  technicians: WizardTech[];
  specialties: WizardSpecialty[];
}

// Columns: one per technician (by tech index), each as wide as that
// tech's WIDEST final bay (never narrower than BAY_WIDTH), placed
// cumulatively with BAY_SPACING between columns — so a 340/360 px
// specialty bay never overlaps the next column, and all-standard columns
// keep the tight 280 + 40 = 320 px pitch. Zero-bay techs still reserve a
// standard-width column, preserving the tech-index layout.
// Rows: per-slot pitch = tallest canonical bay (BAY_HEIGHT) + BAY_SPACING
// = 520 px; specialty bays are shorter, so stacked bays never overlap.
// All inputs are GRID_SIZE multiples, so every position is grid-aligned.
const ROW_HEIGHT_PX = BAY_HEIGHT + BAY_SPACING;
const MARGIN_X_PX = 20;
const MARGIN_Y_PX = 20;

// Mirror of `ShopFloorEditor.bayTypeToCategory` (src/pages/ShopFloorEditor.tsx)
// so the wizard and the layout editor agree on which bay types are
// considered specialty rows. Kept in lockstep so a `wash` chosen via the
// new per-bay dropdown surfaces with the same `bay_category='specialty'`
// the specialty toggle would have produced.
const SPECIALTY_BAY_TYPES = new Set<BayTypeEnum>([
  'adas',
  'alignment',
  'alignment-adas',
  'wash',
  'inspection',
]);

function bayCategoryFor(bayType: BayTypeEnum): 'general' | 'specialty' {
  return SPECIALTY_BAY_TYPES.has(bayType) ? 'specialty' : 'general';
}

export function generateBayLayout({
  technicians,
  specialties,
}: GenerateBayLayoutInput): GeneratedBayPayload[] {
  const out: GeneratedBayPayload[] = [];
  let bayNumber = 1;

  // Group specialties by tech index (preserve insertion order so the
  // "first specialty" → "first bay" mapping is deterministic).
  const specialtyByTech = new Map<number, WizardSpecialty[]>();
  for (const spec of specialties) {
    const list = specialtyByTech.get(spec.primary_tech_index) ?? [];
    list.push(spec);
    specialtyByTech.set(spec.primary_tech_index, list);
  }

  // Pass 1: resolve every tech's FINAL bay types (specialty conversion →
  // per-slot choice → default) so column widths can be sized from them.
  const finalTypesByTech: BayTypeEnum[][] = technicians.map((tech, techIdx) => {
    const bayCount = tech.bays_allocated ?? 0;
    const techSpecs = specialtyByTech.get(techIdx) ?? [];
    const operatorChoices = (tech.bay_types ?? []) as ReadonlyArray<BayTypeEnum>;
    const types: BayTypeEnum[] = [];
    for (let slot = 0; slot < bayCount; slot++) {
      const specForSlot = techSpecs[slot]; // undefined when slot exceeds specialty count
      // Specialty conversion still takes precedence over the dropdown
      // (see the file header) — the existing "specialty consumes one
      // of your bays" product rule is preserved verbatim.
      types.push(specForSlot ? specForSlot.kind : (operatorChoices[slot] ?? DEFAULT_SETUP_BAY_TYPE));
    }
    return types;
  });

  // Pass 2: cumulative column x per tech index.
  const columnX: number[] = [];
  let nextColumnX = MARGIN_X_PX;
  for (const types of finalTypesByTech) {
    columnX.push(nextColumnX);
    const columnWidth = Math.max(BAY_WIDTH, ...types.map(t => defaultFootprintForBayType(t).width));
    nextColumnX += columnWidth + BAY_SPACING;
  }

  // Pass 3: emit bays (unchanged numbering / labels / categories / links).
  for (const [techIdx, types] of finalTypesByTech.entries()) {
    for (const [slot, bay_type] of types.entries()) {
      // Sized AFTER the final bay_type is resolved (specialty conversion
      // or per-slot choice), so the footprint always matches the type.
      const { width, height } = defaultFootprintForBayType(bay_type);
      out.push({
        __techIndex: techIdx,
        name: `Bay ${bayNumber}`,
        bay_number: bayNumber,
        bay_type,
        bay_category: bayCategoryFor(bay_type),
        position_x: columnX[techIdx],
        position_y: MARGIN_Y_PX + slot * ROW_HEIGHT_PX,
        width,
        height,
        is_active: true,
      });
      bayNumber++;
    }
  }

  return out;
}
